import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { ABOUT } from "../../data/content";
import { Reveal } from "../motion/Motion";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section
      id="rolunk"
      data-testid="about-section"
      className="relative overflow-hidden bg-white py-20 md:py-32"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-20">
        {/* Text */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-accent">
              <span className="h-px w-10 bg-brand-accent" />
              {ABOUT.overline}
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-brand-primary sm:text-4xl lg:text-5xl">
              {ABOUT.title}
            </h2>
          </Reveal>
          {ABOUT.paragraphs.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.05}>
              <p className="mt-6 text-base leading-relaxed text-brand-primary/70">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.25}>
            <ul className="mt-8 space-y-3">
              {ABOUT.bullets.map((b) => (
                <li key={b} className="flex items-center gap-3 text-brand-primary">
                  <span className="grid h-7 w-7 shrink-0 place-items-center bg-brand-primary text-brand-accent">
                    <Check size={15} strokeWidth={3} />
                  </span>
                  <span className="font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Layered images */}
        <div className="relative lg:col-span-7 lg:h-[560px]">
          <motion.div
            style={{ y: y1 }}
            className="relative z-10 overflow-hidden shadow-[0_30px_60px_-15px_rgba(10,37,64,0.25)] lg:absolute lg:right-0 lg:top-0 lg:w-[62%]"
          >
            <img
              src={ABOUT.images[0]}
              alt="Modern lakóépület"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </motion.div>
          <motion.div
            style={{ y: y2 }}
            className="relative z-20 -mt-16 ml-6 w-[70%] overflow-hidden border-8 border-white shadow-[0_30px_60px_-15px_rgba(10,37,64,0.3)] lg:absolute lg:bottom-0 lg:left-0 lg:mt-0 lg:ml-0 lg:w-[52%]"
          >
            <img
              src={ABOUT.images[1]}
              alt="Szakértő kivitelezés"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
            />
          </motion.div>
          {/* accent block */}
          <div className="absolute -bottom-4 right-8 z-0 hidden h-32 w-32 bg-brand-accent lg:block" />
        </div>
      </div>
    </section>
  );
}
